import { createEffect, createEvent, createStore, sample } from 'effector';
import { httpClient } from '@/api';
import { readJson, writeJson } from '@/lib/storage';
import { lightTokens, darkTokens, type TokenSet } from '@/theme/tokens';
import type { ThemeName } from '@/theme';

const STORAGE_KEY = 'app:theme';

const detectInitial = (): ThemeName => {
  const stored = readJson<ThemeName>(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches)
    return 'dark';
  return 'light';
};

export const themeChanged = createEvent<ThemeName>();
export const themeToggled = createEvent();

export const $themeName = createStore<ThemeName>(detectInitial())
  .on(themeChanged, (_, next) => next)
  .on(themeToggled, (current) => (current === 'light' ? 'dark' : 'light'));

const persistThemeFx = createEffect((name: ThemeName) => {
  writeJson(STORAGE_KEY, name);
});

const syncThemeColorFx = createEffect((name: ThemeName) => {
  if (typeof document === 'undefined') return;
  const color = name === 'dark' ? '#0E0F12' : '#FBFBFD';
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', color);
  document.documentElement.style.background = color;
});

sample({ clock: $themeName, target: [persistThemeFx, syncThemeColorFx] });

// --- remote tokens с бэкенда ---

export type RemoteThemeResponse = {
  name: ThemeName;
  isDefault: boolean;
  tokens: TokenSet;
};

export const fetchThemeFx = createEffect<ThemeName, RemoteThemeResponse>((name) =>
  httpClient.get<RemoteThemeResponse>(`/api/lume/theme/${name}`),
);

export const $remoteTokens = createStore<Record<ThemeName, TokenSet | null>>({
  light: null,
  dark: null,
}).on(fetchThemeFx.doneData, (state, r) => ({ ...state, [r.name]: r.tokens }));

export const $effectiveTokens = $remoteTokens.map((remote) => ({
  light: remote.light ?? lightTokens,
  dark: remote.dark ?? darkTokens,
}));

export const themesFetchAll = createEvent();

sample({
  clock: themesFetchAll,
  fn: () => 'light' as ThemeName,
  target: fetchThemeFx,
});
sample({
  clock: themesFetchAll,
  fn: () => 'dark' as ThemeName,
  target: fetchThemeFx,
});
